import { useEffect, useRef, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { CheckCircle2, Clock, XCircle, ArrowRight } from "lucide-react";
import { api } from "@/lib/api";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState({ status: "polling", data: null });
  const attempts = useRef(0);

  useEffect(() => {
    if (!sessionId) {
      setState({ status: "error", data: null });
      return;
    }

    let cancelled = false;
    const poll = async () => {
      if (cancelled) return;
      if (attempts.current >= 8) {
        setState({ status: "timeout", data: null });
        return;
      }
      attempts.current += 1;
      try {
        const res = await api.get(`/checkout/status/${sessionId}`);
        if (cancelled) return;
        if (res.data.payment_status === "paid") {
          setState({ status: "paid", data: res.data });
          return;
        }
        if (res.data.status === "expired") {
          setState({ status: "expired", data: res.data });
          return;
        }
        setTimeout(poll, 2000);
      } catch (e) {
        if (!cancelled) setTimeout(poll, 2500);
      }
    };

    poll();
    return () => {
      cancelled = true;
    };
  }, [sessionId]);

  return (
    <div className="min-h-screen bg-[#FDFBF7] bg-grain flex items-center justify-center px-6">
      <div className="max-w-md w-full bg-white rounded-2xl p-8 border border-[#E5E7EB] shadow-sm text-center">
        {state.status === "polling" && (
          <>
            <div className="w-14 h-14 mx-auto rounded-full bg-[#F4F1EA] flex items-center justify-center text-[#064E3B]">
              <Clock />
            </div>
            <h1 className="font-display text-2xl font-bold text-[#022C22] mt-5">
              Confirming your payment…
            </h1>
            <p className="text-[#3F6255] mt-2">Hang tight, this takes a few seconds.</p>
          </>
        )}
        {state.status === "paid" && (
          <>
            <div className="w-14 h-14 mx-auto rounded-full bg-[#D1FAE5] flex items-center justify-center text-[#10B981]">
              <CheckCircle2 />
            </div>
            <h1 className="font-display text-2xl font-bold text-[#022C22] mt-5" data-testid="payment-success-heading">
              Payment received!
            </h1>
            <p className="text-[#3F6255] mt-2">
              We&apos;ve emailed your receipt and onboarding checklist. Our team will contact you within 60 minutes.
            </p>
            <Link to="/" className="btn-primary mt-6 inline-flex" data-testid="payment-success-home">
              Back to home <ArrowRight size={16} />
            </Link>
          </>
        )}
        {(state.status === "timeout" || state.status === "expired" || state.status === "error") && (
          <>
            <div className="w-14 h-14 mx-auto rounded-full bg-red-50 flex items-center justify-center text-red-500">
              <XCircle />
            </div>
            <h1 className="font-display text-2xl font-bold text-[#022C22] mt-5">
              {state.status === "expired" ? "Payment session expired" : "Could not confirm payment"}
            </h1>
            <p className="text-[#3F6255] mt-2">
              If you were charged, please WhatsApp us and we&apos;ll resolve it immediately.
            </p>
            <Link to="/#pricing" className="btn-primary mt-6 inline-flex">
              Try again
            </Link>
          </>
        )}
      </div>
    </div>
  );
}
